import type { PillTone } from '@concord/domain';
import { type ButtonHTMLAttributes, type ComponentProps, forwardRef, type ReactNode, useId } from 'react';
import { cn } from './cn';

/* ---------- buttons (.btn) ---------- */

export type ButtonVariant = 'primary' | 'sec' | 'ghost' | 'danger';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'md' | 'lg';
  busy?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', busy, className, type = 'button', disabled, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn('btn', variant !== 'primary' && variant, size === 'lg' && 'lg', className)}
      disabled={disabled || busy}
      data-busy={busy ? 'true' : undefined}
      {...rest}
    >
      {children}
    </button>
  );
});

/* ---------- pills (.pill) ---------- */

export function Pill({
  tone,
  children,
  className,
}: {
  tone: PillTone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span className={cn('pill', `p-${tone}`, className)}>
      <span className="g" />
      {children}
    </span>
  );
}

/* ---------- panels (.panel, .phead, .pbody) ---------- */

export function Panel({ className, ...rest }: ComponentProps<'section'>) {
  return <section className={cn('panel', className)} {...rest} />;
}

export function PanelHead({
  title,
  sub,
  more,
  children,
}: {
  title: ReactNode;
  sub?: ReactNode;
  /** The right-aligned link action ("Open case list →"). */
  more?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="phead">
      <h2>{title}</h2>
      {sub !== undefined && <span className="sub">{sub}</span>}
      {children}
      {more}
    </div>
  );
}

export function PanelMore({ children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button" className="more" {...rest}>
      {children}
    </button>
  );
}

export function PanelBody({ className, ...rest }: ComponentProps<'div'>) {
  return <div className={cn('pbody', className)} {...rest} />;
}

/* ---------- banners (.banner) ---------- */

export function Banner({
  tone,
  title,
  detail,
  actions,
  role = 'status',
}: {
  tone: 'hold' | 'wait' | 'ok' | 'bad';
  title: ReactNode;
  detail?: ReactNode;
  actions?: ReactNode;
  role?: 'status' | 'alert';
}) {
  return (
    <div className={cn('banner', tone)} role={role}>
      <span className="bar" />
      <div>
        <div className="h">{title}</div>
        {detail && <div className="d">{detail}</div>}
      </div>
      {actions && <div className="act">{actions}</div>}
    </div>
  );
}

/* ---------- key-value lists (.kv) ---------- */

export function KeyValue({ rows }: { rows: [ReactNode, ReactNode][] }) {
  return (
    <dl className="kv">
      {rows.map(([k, v], i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional and static per render
        <Row key={i} k={k} v={v} />
      ))}
    </dl>
  );
}
function Row({ k, v }: { k: ReactNode; v: ReactNode }) {
  return (
    <>
      <dt>{k}</dt>
      <dd>{v}</dd>
    </>
  );
}

/* ---------- forms (.field) ---------- */

export function Field({
  label,
  hint,
  error,
  children,
  className,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  /** Receives the id to put on the control, so the label is associated. */
  children: (id: string, describedBy: string | undefined) => ReactNode;
  className?: string;
}) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const describedBy = [hintId, errId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={cn('field', className)}>
      <label htmlFor={id}>{label}</label>
      {children(id, describedBy)}
      {hint && (
        <span className="hint" id={hintId}>
          {hint}
        </span>
      )}
      {error && (
        <span className="err" id={errId} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

/* ---------- small text atoms ---------- */

export const Key = ({ children, className }: { children: ReactNode; className?: string }) => (
  <span className={cn('key', className)}>{children}</span>
);
export const Ref = ({ children, className }: { children: ReactNode; className?: string }) => (
  <span className={cn('ref', className)}>{children}</span>
);
export const Sub = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={cn('sub2', className)}>{children}</div>
);

/** A masked name: first initial, then the fixed mask in the muted mono face (CON-03). */
export function Masked({ masked }: { masked: string }) {
  return (
    <>
      {masked.charAt(0)}
      <span className="msk">{masked.slice(1)}</span>
    </>
  );
}

export function Notice({ tone, children }: { tone?: 'bad'; children: ReactNode }) {
  return (
    <div className={cn('notice', tone)} role={tone === 'bad' ? 'alert' : undefined}>
      {children}
    </div>
  );
}

/* ---------- spinner ---------- */

/** A spinning ring with its label: visible beside the ring, or for screen readers only. */
export function Spinner({ label = 'Loading…', showLabel = true }: { label?: string; showLabel?: boolean }) {
  return (
    <span className="spinner" role="status" aria-live="polite">
      <span className="ring" aria-hidden="true" />
      <span className={showLabel ? undefined : 'sr-only'}>{label}</span>
    </span>
  );
}
